import { useMemo, useState } from "react";
import { IconFileZip, IconRefresh } from "@tabler/icons-react";
import { getRouteApi } from "@tanstack/react-router";
import { BotonExportarExcel, Button, Card, Group, IconAction, PageHeader, PaginadorTabla, Skeleton, Stack, Text } from "@/components/ui";
import type { ColumnaExcel } from "@/shared/export/exportar-excel";
import { useBusquedaHistorico, useExportarZip, useFiltrosHistorico } from "../hooks/use-historico";
import {
  buscarHistorico,
  type DocumentoHistorico,
  type HistoricoParams,
} from "../services/historico.api";
import { HistoricoFiltrosCard, type FiltrosEnEdicion } from "./HistoricoFiltrosCard";
import { HistoricoTable, formatearTamano } from "./HistoricoTable";

const COLUMNAS_EXCEL: ColumnaExcel<DocumentoHistorico>[] = [
  { header: "Folio", value: (d) => d.folio },
  { header: "Fecha solicitud", value: (d) => d.fechaSolicitud },
  {
    header: "Proveedor(es)",
    value: (d) =>
      d.proveedores.map((p) => `${p.razonSocial} (${p.rfc} · ${p.numeroAcreedor})`).join(" | "),
  },
  { header: "Archivo", value: (d) => d.nombreArchivo },
  { header: "Clasificación", value: (d) => d.clasificacion },
  { header: "Tamaño", value: (d) => formatearTamano(d.tamanoBytes) },
  { header: "Sociedad", value: (d) => d.organizacionNombre },
];

const PAGE_SIZE = 15;
const route = getRouteApi("/_authed-tw/historico-documental");

/**
 * Histórico Documental (P37 · spec §7) · pantalla única de consulta del acervo
 * documental de las solicitudes de pago para roles internos. Los filtros viven
 * en los SEARCH PARAMS de la ruta (consulta compartible por URL, back/forward
 * del navegador funcional) y se aplican al click en Buscar. El aislamiento por
 * sociedad es del SERVIDOR (RN-01) · aquí solo se refleja lo visible.
 */
export function HistoricoDocumentalPage() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const { data: combos, isPending: combosPendientes } = useFiltrosHistorico();

  // Valores en edición · arrancan de la URL; sin fechas en la URL se muestran
  // los defaults del backend (mes en curso · RN-04) apenas cargan los combos.
  const [edicion, setEdicion] = useState<FiltrosEnEdicion | null>(null);
  const valores: FiltrosEnEdicion = edicion ?? {
    desde: search.desde ?? combos?.fechaDesdeDefault ?? "",
    hasta: search.hasta ?? combos?.fechaHastaDefault ?? "",
    rfc: search.rfc ?? "",
    acreedor: search.acreedor ?? "",
    organizacionId: search.organizacionId ?? "",
    clasificacion: search.clasificacion ?? "",
  };

  // La búsqueda usa la URL como fuente de verdad (no el estado en edición).
  const params = useMemo<HistoricoParams>(
    () => ({
      desde: search.desde,
      hasta: search.hasta,
      rfc: search.rfc,
      acreedor: search.acreedor,
      organizacionId: search.organizacionId,
      clasificacion: search.clasificacion,
      page: search.page ?? 1,
      pageSize: PAGE_SIZE,
    }),
    [search],
  );

  const { data, isPending, isError, error, refetch, isFetching, isPlaceholderData } =
    useBusquedaHistorico(params);

  const [seleccion, setSeleccion] = useState<ReadonlySet<string>>(new Set());
  const exportar = useExportarZip();

  const buscar = () => {
    setSeleccion(new Set());
    // La URL vuelve a ser la fuente de verdad · el back/forward del navegador
    // se refleja en los inputs.
    setEdicion(null);
    void navigate({
      search: {
        desde: valores.desde || undefined,
        hasta: valores.hasta || undefined,
        rfc: valores.rfc || undefined,
        acreedor: valores.acreedor || undefined,
        organizacionId: valores.organizacionId || undefined,
        clasificacion: valores.clasificacion || undefined,
        page: undefined, // nueva búsqueda · siempre de vuelta a la página 1
      },
    });
  };

  const cambiarPagina = (page: number) =>
    void navigate({ search: (prev) => ({ ...prev, page: page === 1 ? undefined : page }) });

  const documentos = data?.documentos ?? [];
  const total = data?.total ?? 0;
  const pagina = search.page ?? 1;

  return (
    <div className="w-full px-4 py-6 md:px-6">
      <PageHeader
        title="Histórico documental"
        description="Acervo documental de las solicitudes de pago · consulta, vista previa, descarga y exportación por lote."
        actions={
          <Group gap="xs">
            <BotonExportarExcel
              archivo="historico-documental"
              hoja="Documentos"
              columnas={COLUMNAS_EXCEL}
              obtenerFilas={async () => {
                // Todas las páginas de la búsqueda actual (mismos filtros).
                const base = { ...params, pageSize: 100 };
                const todas: DocumentoHistorico[] = [];
                for (let p = 1; ; p++) {
                  const pagina = await buscarHistorico({ ...base, page: p });
                  todas.push(...pagina.documentos);
                  if (todas.length >= pagina.total || pagina.documentos.length === 0) break;
                }
                return todas;
              }}
            />
            <IconAction label="Refrescar" variant="default" size="lg" onClick={() => refetch()} loading={isFetching}>
              <IconRefresh size={16} />
            </IconAction>
          </Group>
        }
      />
      <Stack gap="md">
        {combosPendientes || !combos ? (
          <Card padding="lg">
            <Stack gap="sm">
              <Skeleton height={36} />
              <Skeleton height={36} />
            </Stack>
          </Card>
        ) : (
          <HistoricoFiltrosCard combos={combos} valores={valores} onChange={setEdicion} onBuscar={buscar} buscando={isFetching} />
        )}
        {isPending ? (
          <Card padding="lg">
            <Stack gap="sm">
              <Skeleton height={36} />
              <Skeleton height={36} />
              <Skeleton height={36} />
            </Stack>
          </Card>
        ) : isError ? (
          <Card>
            <Text c="peligro" size="sm">
              Error: {(error as Error).message}
            </Text>
          </Card>
        ) : (
          <>
            <PaginadorTabla total={total} page={pagina} pageSize={PAGE_SIZE} onPageChange={cambiarPagina} variante="superior" />
            <HistoricoTable
              documentos={documentos}
              seleccion={seleccion}
              onSeleccion={setSeleccion}
              buscando={isPlaceholderData}
              mensajeInformativo={data?.mensajeInformativo ?? null}
            />
            {seleccion.size > 0 && (
              <Group gap="sm">
                <Button
                  size="xs"
                  leftSection={<IconFileZip size={14} stroke={1.7} />}
                  loading={exportar.isPending}
                  onClick={() => exportar.mutate([...seleccion], { onSuccess: () => setSeleccion(new Set()) })}
                >
                  Exportar ZIP ({seleccion.size})
                </Button>
              </Group>
            )}
            <PaginadorTabla total={total} page={pagina} pageSize={PAGE_SIZE} onPageChange={cambiarPagina} sustantivo="documentos" />
          </>
        )}
      </Stack>
    </div>
  );
}
